import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Send, X, Eye, Code2, Loader2, Paperclip, CheckCircle2, AlertTriangle } from "lucide-react";

/**
 * Modal for previewing & editing the email before sending a proposal.
 * Props:
 *  - proposal: { id, ref, client:{ name, email, phone }, project:{ title } }
 *  - onClose()
 *  - onSent(result)  // optional callback after success
 */
export default function ProposalSendModal({ proposal, onClose, onSent }) {
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [to, setTo] = useState("");
  const [subject, setSubject] = useState("");
  const [html, setHtml] = useState("");
  const [publicUrl, setPublicUrl] = useState("");
  const [provider, setProvider] = useState(null);
  const [attachPdf, setAttachPdf] = useState(true);
  const [ccAdmin, setCcAdmin] = useState(false);
  const [view, setView] = useState("preview"); // 'preview' | 'html'
  const [testEmail, setTestEmail] = useState("");
  const [sendingTest, setSendingTest] = useState(false);

  useEffect(() => {
    if (!proposal?.id) return;
    const origin = window.location.origin;
    api.get(`/admin/proposals/${proposal.id}/email-preview`, { params: { origin } })
      .then((r) => {
        setTo(r.data.to || "");
        setSubject(r.data.subject || "");
        setHtml(r.data.html || "");
        setPublicUrl(r.data.public_url || "");
        setProvider(r.data.email_provider || null);
      })
      .catch(() => toast.error("Impossible de charger l'aperçu"))
      .finally(() => setLoading(false));
  }, [proposal?.id]);

  const send = async () => {
    if (!to) return toast.error("Email destinataire manquant");
    setSending(true);
    try {
      const r = await api.post(`/admin/proposals/${proposal.id}/send`, {
        to, subject, html, origin: window.location.origin, attach_pdf: attachPdf, cc_admin: ccAdmin,
      });
      if (r.data.sent) {
        toast.success("Email envoyé !");
      } else {
        toast.warning("Statut mis à jour mais l'email n'a pas pu être envoyé (provider non configuré).");
      }
      onSent && onSent(r.data);
      onClose && onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'envoi");
    } finally { setSending(false); }
  };

  const sendTest = async () => {
    const addr = (testEmail || "").trim();
    if (!addr) return toast.error("Renseignez un email de test");
    setSendingTest(true);
    try {
      const r = await api.post(`/admin/proposals/${proposal.id}/send`, {
        subject, html, origin: window.location.origin, attach_pdf: attachPdf, cc_admin: false,
        test_email: addr,
      });
      if (r.data.sent) toast.success(`Aperçu envoyé à ${addr} ✉️`);
      else toast.warning("Aperçu non envoyé (vérifiez la configuration email).");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'envoi de l'aperçu");
    } finally { setSendingTest(false); }
  };

  const openWhatsapp = async () => {
    try {
      const r = await api.get(`/admin/proposals/${proposal.id}/whatsapp-link`, { params: { origin: window.location.origin } });
      window.open(r.data.wa_url, "_blank");
    } catch { toast.error("Erreur"); }
  };

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(publicUrl); toast.success("Lien copié"); }
    catch { toast.error("Impossible de copier"); }
  };

  const providerBadge = provider?.provider === "resend"
    ? { label: "Resend ✓", color: "bg-[#A7F3D0] text-[#065F46]" }
    : provider?.provider === "smtp"
      ? { label: "SMTP ✓", color: "bg-[#67E8F9] text-[#0A1128]" }
      : { label: "Aucun fournisseur configuré", color: "bg-[#FFDCA8] text-[#92400E]" };

  return (
    <div className="fixed inset-0 z-[100] bg-[#0A1128]/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="proposal-send-modal">
      <div className="bg-white rounded-3xl w-full max-w-4xl max-h-[92vh] overflow-hidden shadow-2xl flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-black/8 flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="display font-extrabold text-[#0A1128] text-lg">Envoyer le devis {proposal?.ref}</div>
            <div className="text-xs text-[#64748B]">Aperçu & édition avant envoi · Pièce jointe PDF incluse</div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-[10px] px-2 py-1 rounded-full font-bold uppercase tracking-wider ${providerBadge.color}`} data-testid="email-provider-badge">
              {providerBadge.label}
            </span>
            <button onClick={onClose} data-testid="send-modal-close" className="text-[#64748B] hover:text-[#0A1128]"><X size={20} /></button>
          </div>
        </div>

        {loading ? (
          <div className="flex-1 flex items-center justify-center p-10"><Loader2 className="animate-spin text-[#06B6D4]" size={32} /></div>
        ) : (
          <div className="flex-1 overflow-auto p-6 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="sm:col-span-1">
                <Label>Destinataire</Label>
                <Input data-testid="send-to" value={to} onChange={(e) => setTo(e.target.value)} />
              </div>
              <div className="sm:col-span-2">
                <Label>Objet</Label>
                <Input data-testid="send-subject" value={subject} onChange={(e) => setSubject(e.target.value)} />
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-4 text-sm bg-[#FAFAFA] border border-black/8 rounded-xl p-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input data-testid="send-attach-pdf" type="checkbox" checked={attachPdf} onChange={(e) => setAttachPdf(e.target.checked)} />
                <Paperclip size={14} /> Joindre le PDF
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input data-testid="send-cc-admin" type="checkbox" checked={ccAdmin} onChange={(e) => setCcAdmin(e.target.checked)} />
                Copier l'admin (CC)
              </label>
              <div className="ml-auto flex items-center gap-2 text-xs">
                <span className="text-[#64748B]">Lien client :</span>
                <code className="bg-white border border-black/8 rounded px-2 py-1 text-[10px] max-w-[200px] truncate">{publicUrl}</code>
                <button onClick={copyLink} className="text-[#06B6D4] font-bold hover:underline">Copier</button>
              </div>
            </div>

            {/* Toggle preview/html */}
            <div className="flex gap-2 text-xs font-bold">
              <button onClick={() => setView("preview")} data-testid="send-view-preview"
                className={`px-3 py-1.5 rounded-full inline-flex items-center gap-1 ${view === "preview" ? "bg-[#0A1128] text-white" : "bg-[#FAFAFA] text-[#64748B] hover:bg-black/5"}`}><Eye size={12} /> Aperçu</button>
              <button onClick={() => setView("html")} data-testid="send-view-html"
                className={`px-3 py-1.5 rounded-full inline-flex items-center gap-1 ${view === "html" ? "bg-[#0A1128] text-white" : "bg-[#FAFAFA] text-[#64748B] hover:bg-black/5"}`}><Code2 size={12} /> HTML brut</button>
            </div>

            {view === "preview" ? (
              <div className="bg-[#FAFAFA] border border-black/8 rounded-2xl p-4 max-h-[40vh] overflow-auto" data-testid="send-preview">
                <iframe srcDoc={html} title="email-preview" className="w-full h-[40vh] bg-white border border-black/8 rounded-xl" sandbox="" />
              </div>
            ) : (
              <Textarea data-testid="send-html-edit" value={html} onChange={(e) => setHtml(e.target.value)} rows={14} className="font-mono text-xs" />
            )}

            {provider?.provider === "none" && (
              <div className="bg-[#FFDCA8]/40 border border-[#FFDCA8] rounded-xl p-3 text-xs text-[#92400E] flex items-start gap-2" data-testid="send-no-provider-warning">
                <AlertTriangle size={14} className="mt-0.5" />
                <div>
                  <b>Aucun fournisseur email configuré.</b> L'envoi sera tracé en interne mais l'email ne partira pas tant qu'une clé Resend (recommandée) ou des credentials SMTP ne sont pas renseignés. <a href="/admin/parametres" className="underline font-bold">Configurer maintenant →</a>
                </div>
              </div>
            )}

            {provider?.provider === "resend" && provider.sender_email?.includes("onboarding@resend.dev") && (
              <div className="bg-[#FFDCA8]/40 border border-[#FFDCA8] rounded-xl p-3 text-xs text-[#92400E] flex items-start gap-2" data-testid="send-resend-default-warning">
                <AlertTriangle size={14} className="mt-0.5" />
                <div>Vous utilisez l'adresse <b>onboarding@resend.dev</b>. Pour envoyer depuis votre domaine (<i>devis@besson-solutions.fr</i>), vérifiez-le sur Resend Dashboard puis renseignez <code>SENDER_EMAIL</code> dans le backend.</div>
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="px-6 py-4 border-t border-black/8 flex flex-wrap items-center gap-2 justify-between bg-[#FAFAFA]">
          <button onClick={openWhatsapp} className="text-sm text-[#0A1128] hover:underline font-semibold flex items-center gap-1" data-testid="send-whatsapp">
            <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><path d="M20.52 3.48A12 12 0 0 0 3.45 20.41L2 22l1.65-.45A12 12 0 1 0 20.52 3.48ZM12 21a9 9 0 0 1-4.6-1.27l-.33-.2-3.18.87.85-3.1-.21-.34A9 9 0 1 1 12 21Zm5.34-6.42c-.29-.14-1.72-.85-2-.95s-.46-.14-.65.14-.74.93-.91 1.12-.33.21-.63.07a7.36 7.36 0 0 1-3.66-3.2c-.27-.47.27-.44.78-1.46a.5.5 0 0 0 0-.47c-.07-.14-.65-1.57-.89-2.15s-.47-.49-.65-.5h-.55a1.07 1.07 0 0 0-.77.36 3.24 3.24 0 0 0-1 2.42 5.65 5.65 0 0 0 1.17 3 12.93 12.93 0 0 0 4.93 4.36c.69.3 1.23.48 1.66.61a4 4 0 0 0 1.84.12 3 3 0 0 0 2-1.39 2.46 2.46 0 0 0 .17-1.4c-.07-.13-.27-.21-.56-.35Z" /></svg>
            Envoyer par WhatsApp
          </button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} data-testid="send-cancel">Annuler</Button>
            <Button onClick={send} disabled={sending || !to} data-testid="send-confirm"
              className="bg-[#0A1128] hover:bg-[#14213d] text-white font-bold">
              {sending ? <><Loader2 className="animate-spin mr-2" size={14} />Envoi…</> : <><Send size={14} className="mr-2" />Envoyer maintenant</>}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
